/**
 * ToolsTab - Gestionare Unelte și Vehicule
 * Actualizat pentru Multi-Tenancy
 */
import { useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, Car, Edit2, Upload, Camera, FileText, Download, Calendar, Hash, Sparkles, Loader2 } from "lucide-react";
import { HelpTooltip } from "@/components/HelpTooltip";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ToolsTab({ tools, vehicles, onRefresh, companyId }) {
  const [openToolDialog, setOpenToolDialog] = useState(false);
  const [openVehicleDialog, setOpenVehicleDialog] = useState(false);
  const [newTool, setNewTool] = useState({ name: "", description: "", purchase_date: "", invoice_number: "" });
  const [newVehicle, setNewVehicle] = useState({ plate: "", name: "", model_year: "", vehicle_type: "Van", initial_km: "" });
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [editVehicleKm, setEditVehicleKm] = useState("");
  const [loading, setLoading] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const extractInvoiceData = async (file) => {
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API}/tools/extract-invoice`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000
      });
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        setNewTool(prev => ({
          ...prev,
          name: d.product_name || prev.name,
          description: d.description || prev.description,
          purchase_date: d.purchase_date || prev.purchase_date,
          invoice_number: d.invoice_number || prev.invoice_number
        }));
        toast.success("Date extrase automat din factură!");
      } else {
        toast.info("Nu am putut extrage datele automat. Completează manual.");
      }
    } catch (error) {
      console.error("Extract error:", error);
      toast.info("Extragerea automată nu a funcționat. Completează manual.");
    } finally {
      setExtracting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("Fișierul este prea mare (max 10MB)");
      return;
    }
    setInvoiceFile(file);
    if (file.type.startsWith("image/")) {
      setInvoicePreview(URL.createObjectURL(file));
    } else {
      setInvoicePreview(null);
    }
    toast.success(`Fișier selectat: ${file.name}`);
    // Extrage automat datele din factură cu AI
    extractInvoiceData(file);
  };

  const handleAddTool = async (e) => {
    e.preventDefault();
    
    if (!companyId) {
      toast.error("Sesiune expirată. Vă rugăm să vă re-logați.");
      return;
    }
    
    if (!newTool.name) {
      toast.error("Introduceți numele uneltei!");
      return;
    }
    
    setLoading(true);
    try {
      // Create tool
      const toolData = {
        name: newTool.name,
        description: newTool.description,
        purchase_date: newTool.purchase_date || null,
        invoice_number: newTool.invoice_number || null
      };
      const response = await axios.post(`${API}/tools`, toolData, { params: { company_id: companyId } });
      const createdTool = response.data;
      
      // Upload invoice if selected
      if (invoiceFile && createdTool.id) {
        const formData = new FormData();
        formData.append("file", invoiceFile);
        formData.append("company_id", companyId);
        await axios.post(`${API}/tools/${createdTool.id}/upload-invoice?company_id=${companyId}`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      
      toast.success("Unealtă adăugată cu succes!");
      setNewTool({ name: "", description: "", purchase_date: "", invoice_number: "" });
      setInvoiceFile(null);
      setInvoicePreview(null);
      setOpenToolDialog(false);
      onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Eroare la adăugare unealtă";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTool = async (id, name) => {
    if (!confirm(`Sigur doriți să ștergeți unealta "${name}"?`)) return;
    try {
      await axios.delete(`${API}/tools/${id}`, { params: { company_id: companyId } });
      toast.success("Unealtă ștearsă!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleDownloadInvoice = (tool) => {
    window.open(`${API}/tools/${tool.id}/invoice?company_id=${companyId}`, "_blank");
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    
    if (!companyId) {
      toast.error("Sesiune expirată. Vă rugăm să vă re-logați.");
      return;
    }
    
    if (!newVehicle.plate || !newVehicle.name) {
      toast.error("Completați numărul și numele vehiculului!");
      return;
    }
    
    setLoading(true);
    try {
      const vehicleData = {
        ...newVehicle,
        model_year: newVehicle.model_year ? parseInt(newVehicle.model_year) : null,
        initial_km: newVehicle.initial_km ? parseFloat(newVehicle.initial_km) : 0,
        current_km: newVehicle.initial_km ? parseFloat(newVehicle.initial_km) : 0,
      };
      await axios.post(`${API}/vehicles`, vehicleData, { params: { company_id: companyId } });
      toast.success("Vehicul adăugat cu succes!");
      setNewVehicle({ plate: "", name: "", model_year: "", vehicle_type: "Van", initial_km: "" });
      setOpenVehicleDialog(false);
      onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Eroare la adăugare vehicul";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (id, name) => {
    if (!confirm(`Sigur doriți să ștergeți vehiculul "${name}"?`)) return;
    try {
      await axios.delete(`${API}/vehicles/${id}`, { params: { company_id: companyId } });
      toast.success("Vehicul șters!");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  const handleUpdateKm = async (vehicleId) => {
    if (!editVehicleKm || isNaN(editVehicleKm)) {
      toast.error("Introduceți un kilometraj valid");
      return;
    }
    try {
      await axios.put(`${API}/vehicles/${vehicleId}`, 
        { current_km: parseFloat(editVehicleKm) },
        { params: { company_id: companyId } }
      );
      toast.success("Kilometraj actualizat!");
      setSelectedVehicle(null);
      setEditVehicleKm("");
      onRefresh();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  return (
    <div className="space-y-6">
      {/* Unelte Firmă */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}} className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-blue-600" />
              Unelte Firmă
              <HelpTooltip section="unelte" />
            </CardTitle>
            <CardDescription>Gestionează inventarul de unelte și echipamente</CardDescription>
          </div>
          <Dialog open={openToolDialog} onOpenChange={(open) => {
            setOpenToolDialog(open);
            if (!open) {
              setInvoiceFile(null);
              setInvoicePreview(null);
              setNewTool({ name: "", description: "", purchase_date: "", invoice_number: "" });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-tool-button">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Unealtă
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-blue-700">
                  <Wrench className="w-5 h-5" />
                  Unealtă Nouă
                </DialogTitle>
                <DialogDescription>
                  Adaugă o unealtă în inventar cu factura de achiziție.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddTool} className="space-y-4">
                <div>
                  <Label htmlFor="tool-name">Nume Unealtă *</Label>
                  <Input
                    id="tool-name"
                    value={newTool.name}
                    onChange={(e) => setNewTool({...newTool, name: e.target.value})}
                    placeholder="Ex: Betonieră 250L DeWalt"
                    required
                    data-testid="tool-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="tool-description">Descriere (opțional)</Label>
                  <Input
                    id="tool-description"
                    value={newTool.description}
                    onChange={(e) => setNewTool({...newTool, description: e.target.value})}
                    placeholder="Ex: Serie ABC123, garanție 2 ani"
                    data-testid="tool-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="purchase-date" className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Data Achiziție
                    </Label>
                    <Input
                      id="purchase-date"
                      type="date"
                      value={newTool.purchase_date}
                      onChange={(e) => setNewTool({...newTool, purchase_date: e.target.value})}
                      data-testid="tool-purchase-date"
                    />
                  </div>
                  <div>
                    <Label htmlFor="invoice-number" className="flex items-center gap-1">
                      <Hash className="w-3 h-3" /> Nr. Factură
                    </Label>
                    <Input
                      id="invoice-number"
                      value={newTool.invoice_number}
                      onChange={(e) => setNewTool({...newTool, invoice_number: e.target.value})}
                      placeholder="Ex: FV-00123"
                      data-testid="tool-invoice-number"
                    />
                  </div>
                </div>

                {/* Upload Factură */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <FileText className="w-3 h-3" /> Factură / Bon (PDF sau Imagine)
                  </Label>
                  <div className="flex gap-2">
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <input 
                      type="file" 
                      ref={cameraInputRef}
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button 
                      type="button"
                      variant="outline" 
                      className="flex-1 border-blue-300 text-blue-600 hover:bg-blue-50"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-invoice-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Încarcă Fișier
                    </Button>
                    <Button 
                      type="button"
                      variant="outline"
                      className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
                      onClick={() => cameraInputRef.current?.click()}
                      data-testid="camera-invoice-btn"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Fotografiază
                    </Button>
                  </div>
                  {invoiceFile && (
                    <div className="p-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 text-sm text-green-700">
                        <FileText className="w-4 h-4" />
                        <span className="truncate flex-1">{invoiceFile.name}</span>
                        <Badge className="bg-green-600 text-xs">{(invoiceFile.size / 1024).toFixed(0)} KB</Badge>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 h-6 w-6 p-0"
                          onClick={() => { setInvoiceFile(null); setInvoicePreview(null); }}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      {invoicePreview && (
                        <img src={invoicePreview} alt="Preview" className="mt-2 rounded max-h-32 object-contain" />
                      )}
                    </div>
                  )}
                  {extracting && (
                    <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700" data-testid="ai-extracting">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <Sparkles className="w-4 h-4" />
                      <span>AI analizează factura și extrage datele...</span>
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="submit-tool-button"
                >
                  {loading ? "Se adaugă..." : "Adaugă Unealtă"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {tools.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nu aveți unelte înregistrate încă.</p>
              <p className="text-sm">Apăsați butonul pentru a adăuga prima unealtă.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tools.map((tool) => (
                <div 
                  key={tool.id} 
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border hover:shadow-md transition-shadow"
                  data-testid={`tool-card-${tool.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Wrench className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold">{tool.name}</p>
                      {tool.description && <p className="text-sm text-slate-600 truncate">{tool.description}</p>}
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                        <span className="text-xs text-slate-500">{tool.total_hours?.toFixed(1) || 0} ore utilizare</span>
                        {tool.purchase_date && (
                          <span className="text-xs text-blue-600 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            Achiziție: {new Date(tool.purchase_date).toLocaleDateString('ro-RO')}
                          </span>
                        )}
                        {tool.invoice_number && (
                          <span className="text-xs text-green-600 flex items-center gap-1">
                            <Hash className="w-3 h-3" />
                            Factură: {tool.invoice_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0 ml-2">
                    {(tool.invoice_filename || tool.invoice_url) && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDownloadInvoice(tool)}
                        className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        title="Descarcă factura"
                        data-testid={`download-invoice-${tool.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteTool(tool.id, tool.name)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-tool-${tool.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vehicule Firmă */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle style={{fontFamily: 'Space Grotesk, sans-serif'}} className="flex items-center gap-2">
              <Car className="w-5 h-5 text-teal-600" />
              Vehicule Firmă
            </CardTitle>
            <CardDescription>Gestionează flota de mașini</CardDescription>
          </div>
          <Dialog open={openVehicleDialog} onOpenChange={setOpenVehicleDialog}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-vehicle-button">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Vehicul
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-teal-700">
                  <Car className="w-5 h-5" />
                  Vehicul Nou
                </DialogTitle>
                <DialogDescription>Adaugă un vehicul nou în flota firmei.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddVehicle} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="vehicle-plate">Număr Înmatriculare *</Label>
                    <Input
                      id="vehicle-plate"
                      value={newVehicle.plate}
                      onChange={(e) => setNewVehicle({...newVehicle, plate: e.target.value.toUpperCase()})}
                      placeholder="IS 01 ABC"
                      required
                      data-testid="vehicle-plate-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vehicle-name">Marca / Model *</Label>
                    <Input
                      id="vehicle-name"
                      value={newVehicle.name}
                      onChange={(e) => setNewVehicle({...newVehicle, name: e.target.value})}
                      placeholder="Dacia Dokker"
                      required
                      data-testid="vehicle-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="vehicle-year">An fabricație</Label>
                    <Input
                      id="vehicle-year"
                      type="number"
                      value={newVehicle.model_year}
                      onChange={(e) => setNewVehicle({...newVehicle, model_year: e.target.value})}
                      placeholder="2023"
                      data-testid="vehicle-year-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vehicle-type">Tip</Label>
                    <Select value={newVehicle.vehicle_type} onValueChange={(val) => setNewVehicle({...newVehicle, vehicle_type: val})}>
                      <SelectTrigger data-testid="vehicle-type-select">
                        <SelectValue placeholder="Tip vehicul" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Van">Utilitară</SelectItem>
                        <SelectItem value="Truck">Camion</SelectItem>
                        <SelectItem value="Car">Autoturism</SelectItem>
                        <SelectItem value="SUV">SUV</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="vehicle-km">Km inițiali</Label>
                    <Input
                      id="vehicle-km"
                      type="number"
                      value={newVehicle.initial_km}
                      onChange={(e) => setNewVehicle({...newVehicle, initial_km: e.target.value})}
                      placeholder="0"
                      data-testid="vehicle-km-input"
                    />
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  disabled={loading}
                  data-testid="submit-vehicle-button"
                >
                  {loading ? "Se adaugă..." : "Adaugă Vehicul"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {vehicles.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nu aveți vehicule înregistrate.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {vehicles.map((vehicle) => (
                <div 
                  key={vehicle.id} 
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-white rounded-lg border hover:shadow-md transition-shadow gap-2"
                  data-testid={`vehicle-card-${vehicle.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                      <Car className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono">{vehicle.plate}</Badge>
                        <span className="font-semibold">{vehicle.name}</span>
                        {vehicle.model_year && <span className="text-xs text-slate-500">({vehicle.model_year})</span>}
                      </div>
                      <div className="flex gap-4 mt-1">
                        <span className="text-xs text-slate-500">{vehicle.vehicle_type} • {(vehicle.current_km || 0).toLocaleString()} km</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {selectedVehicle?.id === vehicle.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={editVehicleKm}
                          onChange={(e) => setEditVehicleKm(e.target.value)}
                          placeholder="Km actuali"
                          className="w-28 h-8"
                          data-testid={`edit-km-input-${vehicle.id}`}
                        />
                        <Button size="sm" onClick={() => handleUpdateKm(vehicle.id)} data-testid={`save-km-${vehicle.id}`}>
                          Salvează
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setSelectedVehicle(null)}>
                          Anulare
                        </Button>
                      </div>
                    ) : (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setSelectedVehicle(vehicle); setEditVehicleKm(vehicle.current_km?.toString() || ""); }}
                        className="text-teal-600 hover:text-teal-800 hover:bg-teal-50"
                        data-testid={`edit-vehicle-${vehicle.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                    )}
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteVehicle(vehicle.id, vehicle.name)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-vehicle-${vehicle.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
